import { Eye, FilePdf, Trash, VideoCamera } from "@phosphor-icons/react"
import { Image } from "@vesatogo/grass-core"
import { getExtension } from "@vesatogo/utils"
import classNames from "classnames"
import { useState } from "react"
import AttachmentModal from "./AttachmentModal"
import FileUploadButton from "./FIleUploadButton"
import { FormCard } from "./FormCard"

type Attachment = {
  id?: string | number
  url?: string
  media?: {
    url: string
    kind?: string
  }
  send_whatsapp?: boolean
}

type AttachmentsProps = {
  attachments: Attachment[]
  onChange: (attachments: Attachment[]) => void
  disabled?: boolean
  maxFiles?: number
  title?: string
  subtitle?: string
}

export const Attachments = ({
  attachments = [],
  onChange,
  disabled = false,
  maxFiles = 8,
  title = "Attachment",
  subtitle = "(Attach a file (max 10 MB). Supported formats: PDF, JPG, PNG, DOC, MP4, WEBM, MOV, AVI.)",
}: AttachmentsProps) => {
  const [loading, setLoading] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const handleUploadSuccess = (uploadedFiles: any[]) => {
    const newAttachments = uploadedFiles.map(file => ({
      media: file.data.media,
      send_whatsapp: false,
    }))
    onChange([...attachments, ...newAttachments])
  }

  const handleTogglePrimary = (index: number) => {
    if (disabled) return
    const updated = attachments.map((item, i) => ({
      ...item,
      send_whatsapp: i === index,
    }))
    onChange(updated)
  }

  const handleDelete = (index: number) => {
    if (disabled) return
    const updated = attachments.filter((_, i) => i !== index)
    onChange(updated)
  }

  return (
    <FormCard
      title={
        <div className="flex items-baseline gap-2">
          <span>{title}</span>
          {subtitle && (
            <span className="text-[10px] font-400  text-blue-400">
              {subtitle}
            </span>
          )}
        </div>
      }
    >
      <div className="flex flex-wrap gap-4 items-start py-2">
        {/* Upload Button Wrapper */}
        <div
          className={classNames(
            "flex flex-col items-center justify-center w-[133px] h-[128px]  rounded-lg hover:border-green-500 transition-colors cursor-pointer relative group",
            disabled && "opacity-50 cursor-not-allowed"
          )}
        >
          <FileUploadButton
            multiple
            disabled={disabled || attachments.length >= maxFiles}
            method="put"
            appName="setutrade"
            environment={import.meta.env.VITE_APP_ENV}
            endpoint={`https://${
              import.meta.env.VITE_REST_URL
            }/rest/trade/uploader/`}
            media={attachments}
            onSuccess={handleUploadSuccess}
            onError={msg => console.error(msg)}
            accept="image/*,application/pdf,video/*"
            maxFileSize={10 * 1024 * 1024} // 10MB
            type="upload"
          />
        </div>

        {/* Existing Attachments */}
        {attachments.map((attachment, index) => {
          const url = attachment.media?.url || attachment.url
          const extension = getExtension(url || "")?.toLowerCase()
          const isPdf = extension === "pdf"
          const isVideo = ["mp4", "webm", "mov", "avi", "mkv"].includes(
            extension || ""
          )
          const isPrimary = !!attachment.send_whatsapp

          return (
            <div
              key={index}
              className={classNames(
                "relative w-[133px] h-[128px] overflow-hidden border-1 transition-all group !rounded-lg",
                isPrimary ? "border-green" : "border-gray-400"
              )}
            >
              {/* File Preview */}
              <div className="p-4 bg-white w-full h-full">
                <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                  {isPdf ? (
                    <div className="flex flex-col items-center text-red-500 bg-white">
                      <FilePdf size={60} weight="fill" className="p-2" />
                      <span className="text-[10px] uppercase font-bold mt-1">
                        PDF
                      </span>
                    </div>
                  ) : isVideo ? (
                    <div className="flex flex-col items-center text-blue-500 bg-white">
                      <VideoCamera size={60} weight="fill" className="p-2" />
                      <span className="text-[10px] uppercase font-bold mt-1">
                        VIDEO
                      </span>
                    </div>
                  ) : (
                    <Image
                      src={url}
                      alt={`attachment-${index}`}
                      className="w-full h-full object-cover rounded p-4"
                    />
                  )}
                </div>
              </div>

              {/* Overlay Actions */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 z-20">
                <button
                  type="button"
                  onClick={() => {
                    if (isPdf || isVideo) {
                      window.open(url, "_blank")
                    } else {
                      setIsPreviewOpen(true)
                    }
                  }}
                  className="bg-white/90 hover:bg-white text-green-600 p-1.5 rounded-full shadow-sm"
                  title="View"
                >
                  <Eye size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(index)}
                  className="bg-white/90 hover:bg-white text-red-500 p-1.5 rounded-full shadow-sm"
                  title="Remove"
                >
                  <Trash size={14} />
                </button>
              </div>

              {/* Radio Button Overlay (Top Left) */}
              <button
                type="button"
                onClick={() => handleTogglePrimary(index)}
                className={classNames(
                  "absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center transition-colors shadow-sm z-30",
                  "bg-white border border-gray-300"
                )}
                title={isPrimary ? "Primary Image" : "Set as Primary"}
              >
                {isPrimary && <div className="w-3 h-3 rounded-full bg-green" />}
              </button>
            </div>
          )
        })}
      </div>

      {isPreviewOpen && (
        <AttachmentModal
          isOpen={isPreviewOpen}
          onClose={() => setIsPreviewOpen(false)}
          attachments={attachments}
          previewOnly
        />
      )}
    </FormCard>
  )
}

export default Attachments
